import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Sesión deslizante: si el backend devuelve un token renovado, lo guardamos.
// Así, mientras el usuario interactúe, la sesión se mantiene viva.
api.interceptors.response.use(
  (response) => {
    const refreshed = response.headers['x-refresh-token'];
    if (refreshed) {
      localStorage.setItem('access_token', refreshed);
    }
    return response;
  },
  (error) => Promise.reject(error)
);

// Public API
export const publicApi = {
  getProducts: (params) => api.get('/products', { params }),
  getProduct: (seoUrl) => api.get(`/products/${seoUrl}`),
  getProductVariations: (seoUrl) => api.get(`/products/${seoUrl}/variations`),
  getServices: () => api.get('/services'),
  getProcesses: () => api.get('/processes'),
  getSuppliers: () => api.get('/suppliers'),
  getSiteConfig: () => api.get('/site-config'),
  getFamilies: () => api.get('/families'),
  createQuote: (data) => api.post('/quotes', data),
  createContact: (data) => api.post('/contact', data),
};

// Admin API
export const adminApi = {
  getDashboard: () => api.get('/admin/dashboard'),
  
  // Products
  getProducts: () => api.get('/admin/products'),
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  
  // Product Variations
  getVariations: (productId) => api.get(`/admin/products/${productId}/variations`),
  createVariation: (productId, data) => api.post(`/admin/products/${productId}/variations`, data),
  updateVariation: (productId, variationId, data) => api.put(`/admin/products/${productId}/variations/${variationId}`, data),
  deleteVariation: (productId, variationId) => api.delete(`/admin/products/${productId}/variations/${variationId}`),
  
  // Services
  getServices: () => api.get('/admin/services'),
  createService: (data) => api.post('/admin/services', data),
  updateService: (id, data) => api.put(`/admin/services/${id}`, data),
  deleteService: (id) => api.delete(`/admin/services/${id}`),
  
  // Processes
  getProcesses: () => api.get('/admin/processes'),
  createProcess: (data) => api.post('/admin/processes', data),
  updateProcess: (id, data) => api.put(`/admin/processes/${id}`, data),
  deleteProcess: (id) => api.delete(`/admin/processes/${id}`),
  
  // Quotes
  getQuotes: (params) => api.get('/admin/quotes', { params }),
  getQuote: (id) => api.get(`/admin/quotes/${id}`),
  updateQuoteStatus: (id, estado) => api.put(`/admin/quotes/${id}/status`, { estado }),
  resendQuote: (id, emails) => api.post(`/admin/quotes/${id}/resend`, { emails }),

  // Site config
  getSiteConfig: () => api.get('/admin/site-config'),
  updateSiteConfig: (configs) => api.put('/admin/site-config', { configs }),
  
  // Suppliers
  getSuppliers: () => api.get('/admin/suppliers'),
  createSupplier: (data) => api.post('/admin/suppliers', data),
  deleteSupplier: (id) => api.delete(`/admin/suppliers/${id}`),

  // Families
  getFamilies: () => api.get('/admin/families'),
  createFamily: (data) => api.post('/admin/families', data),
  updateFamily: (id, data) => api.put(`/admin/families/${id}`, data),
  deleteFamily: (id) => api.delete(`/admin/families/${id}`),

  // Uploads (formData con campo "file")
  uploadImage: (folder, formData) => api.post(`/admin/upload?folder=${folder}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteImage: (path) => api.delete('/admin/upload', { data: { path } }),
};

export default api;
